<template>
  <AnalyticsModuleContainer
    :title-key="config.titleKey"
    :explore-link="config.exploreLink"
    :benchmark-link="config.benchmarkLink"
    :info-content="config.infoContent"
    :member-short-titles="getMembersFromQuery(query)"
  >
    <div
      v-if="!error"
      class="stats-container"
    >
      <AnalyticsExtendedQueryRenderer
        :time-option="{ type: QueryTimeType.PERIOD_WITH_COMPARISON }"
        :query="query"
        @hide-module="hideModule"
      >
        <template #default="{ queryResults }">
          <AnalyticsRendererStats
            :query-results="queryResults"
            :icons="
              config.iconKeys && config.iconKeys.map((iconKey: string)=> $t(iconKey))
            "
            :colors="config.iconKeys && colors"
          />
        </template>
      </AnalyticsExtendedQueryRenderer>
      <AnalyticsExtendedQueryRenderer
        v-if="!config.withoutTrend"
        :time-option="
          config.trendType === TREND_TYPE.RATIO
            ? { type: QueryTimeType.PERIOD }
            : { type: QueryTimeType.PERIOD_WITH_TREND_GRAIN }
        "
        :query="query"
        class="stats-container__trend"
        @hide-module="hideModule"
      >
        <template #default="{ queryResults }">
          <AnalyticsRendererTrend
            :query-results="queryResults"
            :fill-missing-data="config.fillTrendMissingData"
            :trend-type="config.trendType"
            :icons="
              config.iconKeys && config.iconKeys.map((iconKey: string) => $t(iconKey))
            "
          />
        </template>
      </AnalyticsExtendedQueryRenderer>
    </div>
    <AnalyticsModuleErrorTemplate
      v-if="error"
      :error="error"
    />
  </AnalyticsModuleContainer>
</template>

<script lang="ts">
import { mapState } from 'pinia'
import exportedColors from '~/assets/style/_exportDefaultChartColors.module.scss'
import { TREND_TYPE } from '~/constants/renderTypes'
import type { TranslationKeys } from '~/plugins/i18n'
import { getMembersFromQuery } from '~/services/analytics'
import { useAnalyticsStore } from '~/stores/analytics'
import {
  QueryTimeType,
  type ModuleBenchmarkLink,
  type ModuleExplorerLink,
  type ModuleInfoContent
} from '~/types/analytics'
import type { Executable, MinimalQuery } from '~/types/query'

type SingleModuleConfig = {
  titleKey: TranslationKeys
  benchmarkLink?: ModuleBenchmarkLink
  iconKeys?: string[]
  trendType?: TREND_TYPE
  withoutTrend?: boolean
  fillTrendMissingData?: boolean
  exploreLink?: ModuleExplorerLink
  infoContent?: ModuleInfoContent
}

export default {
  name: 'ModuleComponentsSingle',
  props: {
    query: {
      type: Object as PropType<Executable<MinimalQuery>>,
      required: true
    },
    config: {
      type: Object as PropType<SingleModuleConfig>,
      required: true
    }
  },
  setup() {
    return {
      colors: Object.values(exportedColors),
      QueryTimeType,
      TREND_TYPE,
      getMembersFromQuery
    }
  },
  data(): { error: Error | undefined } {
    return {
      error: undefined
    }
  },
  computed: {
    ...mapState(useAnalyticsStore, ['analyticsParams'])
  },
  watch: {
    analyticsParams: {
      handler() {
        this.error = undefined
      }
    }
  },
  methods: {
    hideModule(error: any) {
      this.error = error
    }
  }
}
</script>

<style lang="scss" scoped>
.stats-container {
  display: flex;
  justify-content: space-between;

  &__trend {
    align-items: flex-end;
  }
}
</style>
